import type { CSSProperties, ReactNode, Ref } from 'react';

import { cn } from '#/utils/cn';

interface SkeletonProps {
  className?: string;
  /** Escape hatch for dimensions that come from a constant, not the scale. */
  style?: CSSProperties;
}

/**
 * A single placeholder bar. Size it with the caller's classes — the primitive
 * only owns the radius and the shared `skeleton` shimmer (`src/styles.css`).
 *
 * Bones are decorative: the loading state is announced once by the
 * `SkeletonRegion` that wraps them, so every bar is hidden from the
 * accessibility tree.
 */
export function Skeleton({ className, style }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      style={style}
      className={cn(
        'skeleton h-16 rounded motion-reduce:animate-none',
        className,
      )}
    />
  );
}

interface SkeletonTextProps {
  /** Number of stacked lines. The last one is shortened. */
  lines?: number;
  className?: string;
}

/** A paragraph of placeholder lines, the last one short like real copy. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={cn('flex flex-col gap-8', className)}>
      {Array.from({ length: lines }, (_, line) => (
        <Skeleton
          key={line}
          className={cn('h-[1em]', line === lines - 1 && 'w-2/3')}
        />
      ))}
    </div>
  );
}

interface SkeletonRegionProps {
  /** Announced to screen readers while the region is pending. */
  label: string;
  className?: string;
  /** Exposed so a loading view can hand the region to layout hooks. */
  ref?: Ref<HTMLDivElement>;
  children: ReactNode;
}

/**
 * The status wrapper for a loading view. Mount exactly one per pending screen —
 * it is what announces the wait.
 */
export function SkeletonRegion({
  label,
  className,
  ref,
  children,
}: SkeletonRegionProps) {
  return (
    <div ref={ref} role="status" aria-busy="true" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
